/**
 * The places a job actually goes to.
 *
 * Work used to run from one town to another: *"Take a package from Merton to
 * North Prairie."* That is a waypoint with a name on it, and it made every job
 * the same job. Nobody delivers to a town.
 *
 * So a job runs between **venues** -- a pharmacy, a builders' merchant, a
 * self-storage place -- each sitting in a town, each with a kind that says what
 * it is. That does three things beyond flavour:
 *
 * - **It gives the map something to draw.** A pickup and a drop are now
 *   specific objects with icons rather than two circles.
 * - **It makes the routine legible**, which is the whole subject. §8: the most
 *   quietly alarming thing in one of these datasets is not a crime, it is that
 *   the system knows which Tuesday is the pharmacy. A week of "Merton to North
 *   Prairie" says nothing about anybody. A week that includes the same pharmacy
 *   every Tuesday says a great deal.
 * - **It gives the illegal work somewhere to belong.** A bag left at a
 *   self-storage unit at two in the morning reads differently from a package
 *   taken to a hardware store, and the difference is information a query would
 *   surface.
 *
 * ## They are real
 *
 * These were invented at first -- generated names in generated towns -- and that
 * was wrong for the same reason inventing the road network would have been.
 * Nothing in this county is made up: the roads, the towns, the camera
 * positions and the departments that run them all come from the public record,
 * so the businesses do too, taken from the same OpenStreetMap extract and
 * tagged the way the mappers tagged them.
 *
 * Which also makes the routine argument land properly. A pharmacy in this game
 * is a pharmacy that is there, so a week of driving reconstructs a week
 * somebody could actually have had.
 */

export const VENUE_KINDS = [
  'fuel',
  'pharmacy',
  'grocery',
  'hardware',
  'garage',
  'depot',
  'storage',
  'diner',
  'bar',
  'clinic',
  'office',
  'yard',
  'garden',
] as const;
export type VenueKind = (typeof VENUE_KINDS)[number];

export interface Venue {
  id: string;
  kind: VenueKind;
  /** What it is called, which is a name a person would use. */
  name: string;
  /** The town it is in. */
  town: string;
  /** The junction it sits at. */
  node: number;
  x: number;
  y: number;
}

/**
 * Which venues take which work.
 *
 * A pharmacy does not take a bag with no questions and a scrap yard does not
 * receive a prescription. Pairing the tiers to plausible venues is what stops
 * illegal work reading as a reskin of the legal kind.
 */
export const LEGAL_VENUES: readonly VenueKind[] = Object.freeze([
  'pharmacy', 'grocery', 'hardware', 'garage', 'depot', 'diner', 'clinic', 'garden', 'bar',
]);

/**
 * And the other kind.
 *
 * This was narrowed to facilities for a while, on the reasoning that "leave a
 * bag here, no questions" implies the business is in on it. That reasoning does
 * not hold: **a car park, a diner, a hardware store are all places where
 * something illegal happens without anybody who works there knowing.** That is
 * what a public place is. Somebody handing over a bag in a supermarket car park
 * says nothing whatever about the supermarket.
 *
 * The line section 6 actually draws is at real named *individuals*, and it is
 * held elsewhere: the office tags that return "Firstname Lastname &
 * Associates" are not fetched at all. That is the case where a real person
 * appears. A business with a trading name is not one.
 */
export const ILLEGAL_VENUES: readonly VenueKind[] = Object.freeze([
  'storage', 'yard', 'depot', 'bar', 'garage', 'diner', 'grocery', 'hardware',
]);

/**
 * Where a car can be refuelled.
 *
 * Its own list rather than a venue kind that takes work, because nobody
 * couriers a package to a petrol station -- you go there because the tank is
 * low, which is a different reason to drive somewhere and is the point of it.
 */
export const FUEL_VENUES: readonly VenueKind[] = Object.freeze(['fuel']);

/** A stable id for a real business, from what it is and where it stands. */
export function venueId(name: string, x: number, y: number): string {
  return `${name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}-${Math.round(x)}-${Math.round(y)}`;
}

/** One line describing a job between two venues, for somebody reading a board. */
export function describeRun(
  kind: string,
  from: Venue,
  to: Venue,
  illegal: boolean,
): string {
  if (illegal) {
    return kind === 'collection'
      ? `Collect an envelope from ${from.name}, ${from.town}. Take it to ${to.name}, ${to.town}. Do not open it.`
      : `Leave a bag at ${to.name}, ${to.town}. Pick it up from ${from.name}, ${from.town}. No questions.`;
  }
  switch (kind) {
    case 'ferry':
      return `Somebody at ${from.name}, ${from.town} needs a lift to ${to.name}, ${to.town}.`;
    case 'errand':
      return `Collect an order from ${from.name}, ${from.town} and run it to ${to.name}, ${to.town}.`;
    default:
      return `Take a package from ${from.name}, ${from.town} to ${to.name}, ${to.town}.`;
  }
}
